import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import Head from "next/head";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import { updateBrandData } from "~/services";
import { faBookFont, faEllipsis, faFrame, faGlobe, faHexagonImage, faImage, faInputText, faLock, faPalette, faPenField, faSave, faStore, faTabletButton } from "@fortawesome/pro-regular-svg-icons";
import { getBrandReturnAppDemoUrl, getBrandReturnAppLiveUrl, getDefaultLanguage } from "~/cores/appCore";
import PageInformationCard from "~/components/PageInformationCard.jsx";
import { TurnrCard, TurnrCardContent } from "~/components/card/TurnrCard.jsx";
import { TurnrButton } from "~/components/buttons/TurnrButton";
import { filterChangedValues, useWithOriginalState } from "~/hooks";
import TurnrForm from "~/components/input/TurnrForm.jsx";
import { TurnrConfirmSaveModal } from "~/components/modals/TurnrConfirmModal.jsx";
import { TurnrActionAlert } from "~/components/alerts/TurnrAlert.jsx";
import { LANGUAGE_EN, QUERY_MAP } from "~/constants";
import * as styles from "./AppDesignForm.module.scss";
import classnames from "classnames";
import ShopInfoSettings from "././_settings/ShopInfoSettings";
import LogoSettings from "././_settings/LogoSettings";
import BackgroundImageSettings from "././_settings/BackgroundImageSettings";
import LanguageSettings from "././_settings/LanguageSettings";
import FontSettings from "././_settings/FontSettings";
import GeneralDesignSettings from "././_settings/GeneralDesignSettings";
import LoginSettings from "././_settings/LoginSettings";
import OtherSettings from "././_settings/OtherSettings";
import AdminSettings from "././_settings/AdminSettings";
import SettingCategoryButton from "./SettingCategoryButton";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";
import { useServiceHook } from "rylak-common";
import { isEmailValid } from "rylak-common/src/utils";
import IframePart from "./IframePart";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import ButtonDesignSettings from "././_settings/ButtonDesignSettings";
import InputDesignSettings from "././_settings/InputDesignSettings";
import WindowSettings from "././_settings/WindowSettings";
import { BRAND_ROUNDED_STYLE_TYPES } from "rylak-common/src/constants";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";

export const APP_DESIGN_SETTING_CATEGORY = {
    SHOP_INFO: {
        id: "SHOP_INFO",
        title: "General Shop info",
        // description: <span>The <b>Shop url</b> is the most important setting here to make sure we link correctly to your shop</span>,
        icon: faStore,
        Component: ShopInfoSettings,
    },
    LOGO: {
        id: "LOGO",
        title: "Shop logo",
        description: "",
        icon: faHexagonImage,
        Component: LogoSettings,
    },
    BACKGROUND: {
        id: "BACKGROUND",
        title: "Background",
        description: "",
        icon: faImage,
        Component: BackgroundImageSettings,
    },
    WINDOW: {
        id: "WINDOW",
        title: "Window",
        description: "",
        icon: faFrame,
        Component: WindowSettings,
    },
    LANGUAGES: {
        id: "LANGUAGES",
        title: "Languages",
        description: "",
        icon: faGlobe,
        Component: LanguageSettings,
    },
    FONT: {
        id: "FONT",
        title: "Fonts & Text",
        description: "",
        icon: faBookFont,
        Component: FontSettings,
    },
    BUTTON_DESIGN: {
        id: "BUTTON_DESIGN",
        title: "Button Design",
        description: "",
        icon: faTabletButton,
        Component: ButtonDesignSettings,
    },
    INPUT_DESIGN: {
        id: "INPUT_DESIGN",
        title: "Input Design",
        description: "",
        icon: faInputText,
        Component: InputDesignSettings,
    },
    DESIGN: {
        id: "DESIGN",
        title: "General Design",
        description: "",
        icon: faPalette,
        Component: GeneralDesignSettings,
    },
    LOGIN: {
        id: "LOGIN",
        title: "Login",
        description: "",
        icon: faPenField,
        Component: LoginSettings,
    },
    OTHER: {
        id: "OTHER",
        title: "Other",
        description: "",
        icon: faEllipsis,
        Component: OtherSettings,
    },
    ADMIN: {
        id: "ADMIN",
        title: "Admin",
        description: "",
        icon: faLock,
        Component: AdminSettings,
    },
};

const leftSideGridValues = { xs: 12, md: 5, lg: 4, xl: 3 };

function AppDesignForm(props) {
    const { ref } = props;
    const { appProps, onResetClick } = props;
    const { selectedBrandData, isUserAdmin } = appProps;

    const [iFrameData, setIFrameData] = useState(null);

    const [displayName, setDisplayName] = useWithOriginalState(selectedBrandData?.displayName || "");
    const [logoSrc, setLogoSrc] = useWithOriginalState(selectedBrandData?.logoSrc || "");
    const [logoHeight, setLogoHeight] = useWithOriginalState(selectedBrandData?.settings?.logoHeight, "");
    const [logoHeightMobile, setLogoHeightMobile] = useWithOriginalState(selectedBrandData?.settings?.logoHeightMobile, "");
    const [alternativeLogoSrc, setAlternativeLogoSrc] = useWithOriginalState(selectedBrandData?.settings?.alternativeLogoSrc || "");
    const [alternativeLogoHeight, setAlternativeLogoHeight] = useWithOriginalState(selectedBrandData?.settings?.alternativeLogoHeight || "");
    const [homePage, setHomePage] = useWithOriginalState(selectedBrandData?.settings?.homePage || "");

    const [backgroundFrameColor, setBackgroundFrameColor] = useWithOriginalState(selectedBrandData?.settings?.backgroundFrameColor || "#ffffff");
    const [framePaddingMobile, setFramePaddingMobile] = useWithOriginalState(selectedBrandData?.settings?.framePaddingMobile, "");
    const [framePaddingPc, setFramePaddingPc] = useWithOriginalState(selectedBrandData?.settings?.framePaddingPc, "");
    const [frameBorderEffect, setFrameBorderEffect] = useWithOriginalState(selectedBrandData?.settings?.frameBorderEffect || "");

    const [colorPrimary, setColorPrimary] = useWithOriginalState(selectedBrandData?.colorPrimary || "");
    const [enabledLanguages, setEnabledLanguages] = useWithOriginalState(selectedBrandData?.settings?.enabledLanguages || []);
    const [defaultLanguage, setDefaultLanguage] = useWithOriginalState(getDefaultLanguage(appProps));
    const [backgroundImage, setBackgroundImage] = useWithOriginalState(selectedBrandData?.settings?.backgroundImage || "");
    const [backgroundImageMobile, setBackgroundImageMobile] = useWithOriginalState(selectedBrandData?.settings?.backgroundImageMobile || "");
    const [startBackground, setStartBackground] = useWithOriginalState(selectedBrandData?.settings?.startBackground || "");
    const [successBackground, setSuccessBackground] = useWithOriginalState(selectedBrandData?.settings?.successBackground || "");
    const [backgroundImageOpacity, setBackgroundImageOpacity] = useWithOriginalState(selectedBrandData?.settings?.backgroundImageOpacity || "");
    const [backgroundImageBlur, setBackgroundImageBlur] = useWithOriginalState(selectedBrandData?.settings?.backgroundImageBlur || "");
    const [orderIdText, setOrderIdText] = useWithOriginalState(selectedBrandData?.settings?.orderIdText || "");
    const [orderIdPrefix, setOrderIdPrefix] = useWithOriginalState(selectedBrandData?.settings?.orderIdPrefix || "");
    const [htmlPageTitle, setHtmlPageTitle] = useWithOriginalState(selectedBrandData?.settings?.htmlPageTitle || "");
    const [htmlFavicon, setHtmlFavicon] = useWithOriginalState(selectedBrandData?.settings?.htmlFavicon || "");
    const [supportEmail, setSupportEmail] = useWithOriginalState(selectedBrandData?.settings?.supportEmail || "");

    const [productsBundleMock, setProductsBundleMock] = useWithOriginalState(selectedBrandData?.settings?.productsBundleMock || "");
    const [shopSubdomain, setShopSubdomain] = useWithOriginalState(selectedBrandData?.shopSubdomain || "");
    const [exchangePolicy, setExchangePolicy] = useWithOriginalState(selectedBrandData?.settings?.exchangePolicy || "");
    const [senderEmail, setSenderEmail] = useWithOriginalState(selectedBrandData?.settings?.senderEmail || "");
    const [merchant, setMerchant] = useWithOriginalState(selectedBrandData?.merchant || "");

    const [returnPolicyUrl, setReturnPolicyUrl] = useWithOriginalState(selectedBrandData?.settings?.returnPolicyUrl || "");
    const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useWithOriginalState(selectedBrandData?.settings?.termsAndConditionsUrl || "");
    const [privacyPolicyUrl, setPrivacyPolicyUrl] = useWithOriginalState(selectedBrandData?.settings?.privacyPolicyUrl || "");
    const [cookiesPolicy, setCookiesPolicy] = useWithOriginalState(selectedBrandData?.settings?.cookiesPolicy || "");

    const [fontNamePrimary, setFontNamePrimary] = useWithOriginalState(selectedBrandData?.settings?.fontNamePrimary || "");
    const [textColor, setTextColor] = useWithOriginalState(selectedBrandData?.settings?.textColor || "");
    const [textColorSecondary, setTextColorSecondary] = useWithOriginalState(selectedBrandData?.settings?.textColorSecondary || "");
    const [fontSizeBody, setFontSizeBody] = useWithOriginalState(selectedBrandData?.settings?.fontSizeBody || "");
    const [fontWeightBody, setFontWeightBody] = useWithOriginalState(selectedBrandData?.settings?.fontWeightBody || "");

    const [buttonVariant, setButtonVariant] = useWithOriginalState(selectedBrandData?.settings?.buttonVariant || "");
    const [buttonType, setButtonType] = useWithOriginalState(selectedBrandData?.settings?.buttonType || BRAND_ROUNDED_STYLE_TYPES.ROUNDED);
    const [buttonTextType, setButtonTextType] = useWithOriginalState(selectedBrandData?.settings?.buttonTextType || "");
    const [buttonEffects, setButtonEffects] = useWithOriginalState(selectedBrandData?.settings?.buttonEffects || "");
    const [buttonFont, setButtonFont] = useWithOriginalState(selectedBrandData?.settings?.buttonFont || "");
    const [buttonThickness, setButtonThickness] = useWithOriginalState(selectedBrandData?.settings?.buttonThickness || "");

    const [inputVariant, setInputVariant] = useWithOriginalState(selectedBrandData?.settings?.inputVariant || "");
    const [inputType, setInputType] = useWithOriginalState(selectedBrandData?.settings?.inputType || BRAND_ROUNDED_STYLE_TYPES.ROUNDED);
    const [checkboxType, setCheckboxType] = useWithOriginalState(selectedBrandData?.settings?.checkboxType || BRAND_ROUNDED_STYLE_TYPES.SLIGHTLY_ROUNDED);

    const [windowType, setWindowType] = useWithOriginalState(selectedBrandData?.settings?.windowType || BRAND_ROUNDED_STYLE_TYPES.VERY_ROUNDED);
    const [overallType, setOverallType] = useWithOriginalState(selectedBrandData?.settings?.overallType || BRAND_ROUNDED_STYLE_TYPES.VERY_ROUNDED);

    // Other
    const [successConfetti, setSuccessConfetti] = useWithOriginalState(selectedBrandData?.settings?.successConfetti || "");
    const [productImageAspectRatio, setProductImageAspectRatio] = useWithOriginalState(selectedBrandData?.settings?.productImageAspectRatio || "");
    const [productImageObjectFit, setProductImageObjectFit] = useWithOriginalState(selectedBrandData?.settings?.productImageObjectFit || "");
    const [exchangePropertyPicker, setExchangePropertyPicker] = useWithOriginalState(selectedBrandData?.settings?.exchangePropertyPicker || "");

    // const [supportEmailError, setSupportEmailError] = useState(null);

    const allInputValues = useMemo(() => {
        return {
            displayName,
            logoSrc,
            logoHeight,
            logoHeightMobile,
            alternativeLogoSrc,
            alternativeLogoHeight,
            homePage,
            backgroundFrameColor,
            framePaddingMobile,
            framePaddingPc,
            frameBorderEffect,
            colorPrimary,
            enabledLanguages,
            defaultLanguage,
            backgroundImage,
            backgroundImageMobile,
            startBackground,
            successBackground,
            backgroundImageOpacity,
            backgroundImageBlur,
            successConfetti,
            productImageAspectRatio,
            productImageObjectFit,
            exchangePropertyPicker,
            orderIdText,
            orderIdPrefix,
            htmlPageTitle,
            htmlFavicon,
            supportEmail,
            returnPolicyUrl,
            termsAndConditionsUrl,
            privacyPolicyUrl,
            cookiesPolicy,
            productsBundleMock,
            shopSubdomain,
            exchangePolicy,
            senderEmail,
            merchant,
            fontNamePrimary,
            textColor,
            textColorSecondary,
            fontSizeBody,
            fontWeightBody,
            buttonVariant,
            buttonType,
            buttonTextType,
            buttonEffects,
            buttonFont,
            buttonThickness,
            inputType,
            inputVariant,
            checkboxType,
            windowType,
            overallType,
        };
    }, [
        displayName,
        logoSrc,
        logoHeight,
        logoHeightMobile,
        alternativeLogoSrc,
        alternativeLogoHeight,
        homePage,
        backgroundFrameColor,
        framePaddingMobile,
        framePaddingPc,
        frameBorderEffect,
        colorPrimary,
        enabledLanguages,
        defaultLanguage,
        backgroundImage,
        backgroundImageMobile,
        startBackground,
        successBackground,
        backgroundImageOpacity,
        backgroundImageBlur,
        successConfetti,
        productImageAspectRatio,
        productImageObjectFit,
        exchangePropertyPicker,
        orderIdText,
        orderIdPrefix,
        htmlPageTitle,
        htmlFavicon,
        supportEmail,
        returnPolicyUrl,
        termsAndConditionsUrl,
        privacyPolicyUrl,
        cookiesPolicy,
        productsBundleMock,
        shopSubdomain,
        exchangePolicy,
        senderEmail,
        merchant,
        fontNamePrimary,
        textColor,
        textColorSecondary,
        fontSizeBody,
        fontWeightBody,
        buttonVariant,
        buttonType,
        buttonTextType,
        buttonEffects,
        buttonFont,
        buttonThickness,
        inputType,
        inputVariant,
        checkboxType,
        windowType,
        overallType,
    ]);

    const allInputValuesAndSetters = useMemo(() => {
        return {
            ...allInputValues,
            setDisplayName,
            setLogoSrc,
            setLogoHeight,
            setLogoHeightMobile,
            setAlternativeLogoSrc,
            setAlternativeLogoHeight,
            setHomePage,
            setBackgroundFrameColor,
            setFramePaddingMobile,
            setFramePaddingPc,
            setFrameBorderEffect,
            setColorPrimary,
            setEnabledLanguages,
            setDefaultLanguage,
            setBackgroundImage,
            setBackgroundImageMobile,
            setStartBackground,
            setSuccessBackground,
            setBackgroundImageOpacity,
            setBackgroundImageBlur,
            setSuccessConfetti,
            setProductImageAspectRatio,
            setProductImageObjectFit,
            setExchangePropertyPicker,
            setOrderIdText,
            setOrderIdPrefix,
            setHtmlPageTitle,
            setShopSubdomain,
            setExchangePolicy,
            setSenderEmail,
            setMerchant,
            setFontNamePrimary,
            setTextColor,
            setTextColorSecondary,
            setFontSizeBody,
            setFontWeightBody,
            setButtonVariant,
            setButtonType,
            setButtonTextType,
            setButtonEffects,
            setButtonFont,
            setButtonThickness,
            setInputType,
            setInputVariant,
            setCheckboxType,
            setWindowType,
            setOverallType,
            setHtmlFavicon,
            setSupportEmail,
            setReturnPolicyUrl,
            setTermsAndConditionsUrl,
            setPrivacyPolicyUrl,
            setCookiesPolicy,
            setProductsBundleMock,
        };
    }, [
        allInputValues,
        setDisplayName,
        setLogoSrc,
        setLogoHeight,
        setLogoHeightMobile,
        setAlternativeLogoSrc,
        setAlternativeLogoHeight,
        setHomePage,
        setBackgroundFrameColor,
        setFramePaddingMobile,
        setFramePaddingPc,
        setFrameBorderEffect,
        setColorPrimary,
        setEnabledLanguages,
        setDefaultLanguage,
        setBackgroundImage,
        setBackgroundImageMobile,
        setStartBackground,
        setSuccessBackground,
        setBackgroundImageOpacity,
        setBackgroundImageBlur,
        setSuccessConfetti,
        setProductImageAspectRatio,
        setProductImageObjectFit,
        setExchangePropertyPicker,
        setOrderIdText,
        setOrderIdPrefix,
        setHtmlPageTitle,
        setShopSubdomain,
        setExchangePolicy,
        setSenderEmail,
        setMerchant,
        setFontNamePrimary,
        setTextColor,
        setTextColorSecondary,
        setFontSizeBody,
        setFontWeightBody,
        setButtonVariant,
        setButtonType,
        setButtonTextType,
        setButtonEffects,
        setButtonFont,
        setButtonThickness,
        setInputType,
        setInputVariant,
        setCheckboxType,
        setWindowType,
        setOverallType,
        setHtmlFavicon,
        setSupportEmail,
        setReturnPolicyUrl,
        setTermsAndConditionsUrl,
        setPrivacyPolicyUrl,
        setCookiesPolicy,
        setProductsBundleMock,
    ]);

    const [selectedDetailsId, setSelectedDetailsId, , initializedSelectedDetailsId] = useQueryState({ key: QUERY_MAP.APP_DESIGN_FORM_DETAILS });
    const [selectedDetails, setSelectedDetails] = useState(APP_DESIGN_SETTING_CATEGORY[selectedDetailsId]);

    useEffect(() => {
        const newSelectedDetails = APP_DESIGN_SETTING_CATEGORY[selectedDetailsId];
        if (newSelectedDetails) {
            setSelectedDetails(newSelectedDetails);
        }
    }, [selectedDetailsId]);

    const updateSelectedDetails = useCallback(
        (id) => {
            setSelectedDetailsId(id);
        },
        [setSelectedDetailsId],
    );

    const serviceBody = useMemo(() => {
        return filterChangedValues(allInputValues);
    }, [allInputValues]);

    useImperativeHandle(
        ref,
        () => ({
            confirmNavigation() {
                return Object.keys(serviceBody).length > 0;
            },
        }),
        [serviceBody],
    );

    let { serviceData: updateBrandDataService, fetchServiceData: triggerUpdateBrandDataFetch } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchFn: updateBrandData,
    });

    const handleSaveConfirm = useCallback(() => {
        triggerUpdateBrandDataFetch({
            brandId: selectedBrandData.id,
            body: serviceBody,
        });
    }, [serviceBody, selectedBrandData]);

    useEffect(() => {
        if (enabledLanguages?.value && enabledLanguages?.value?.length > 0 && !enabledLanguages?.value?.includes(defaultLanguage.value)) {
            setDefaultLanguage(enabledLanguages?.value?.includes(LANGUAGE_EN) ? LANGUAGE_EN : enabledLanguages?.value[0]);
        }
    }, [enabledLanguages, defaultLanguage]);

    const DetailsComponent = selectedDetails?.Component;

    const [confirmChangesModalOpen, setConfirmChangesModalOpen] = useState(false);

    // Need a better system for errors
    const supportEmailError = allInputValues?.supportEmail?.value && !isEmailValid(allInputValues.supportEmail?.value);

    const onSaveClick = useCallback(() => {
        // This should probably be handled much more automatic and better
        if (supportEmailError) {
            // setSupportEmailError("The support email you have provided is");
            appProps?.openSnackbarError({
                content: "The support email you have chosen is invalid. Please correct it and try again",
            });
        } else {
            setConfirmChangesModalOpen(true);
        }
    }, [allInputValues, supportEmailError]);

    return (
        <>
            <Head>
                <title>Design Settings</title>
            </Head>
            <TurnrConfirmSaveModal
                open={confirmChangesModalOpen}
                onClose={setConfirmChangesModalOpen}
                appProps={appProps}
                serviceData={updateBrandDataService}
                onConfirm={handleSaveConfirm}
                title={"App design changes"}
                confirmButtonText={"Confirm"}
                text={<TurnrActionAlert>These changes will take immediate effect</TurnrActionAlert>}
                onSuccess={() => appProps.triggerFetchBrand()}
            />
            <TurnrForm onSubmit={onSaveClick}>
                <TurnrGrid container spacing={2}>
                    <TurnrGrid container spacing={2} item {...leftSideGridValues}>
                        <TurnrGrid item xs={12}>
                            <PageInformationCard
                                {...props}
                                icon={faPalette}
                                title={"App Design"}
                                content={
                                    <div>
                                        <p> You can login with any email and order ID in the app design preview. All data is offline.</p>
                                    </div>
                                }
                            />
                            {initializedSelectedDetailsId && (
                                <div className={styles["settings-wrapper"]}>
                                    <div className={classnames(styles["settings"], !selectedDetailsId && styles["settings-show"])}>
                                        <TurnrCard>
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.SHOP_INFO}
                                                detectChangeValues={[displayName, homePage, htmlPageTitle, htmlFavicon, supportEmail]}
                                                error={supportEmailError}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.LOGO}
                                                detectChangeValues={[logoSrc, logoHeight, logoHeightMobile, alternativeLogoSrc, alternativeLogoHeight]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.BACKGROUND}
                                                detectChangeValues={[backgroundImage, backgroundImageMobile, startBackground, successBackground]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.WINDOW}
                                                detectChangeValues={[
                                                    windowType,
                                                    backgroundFrameColor,
                                                    backgroundImageOpacity,
                                                    backgroundImageBlur,
                                                    framePaddingPc,
                                                    framePaddingMobile,
                                                    frameBorderEffect,
                                                ]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.LANGUAGES}
                                                detectChangeValues={[enabledLanguages, defaultLanguage]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.FONT}
                                                detectChangeValues={[fontNamePrimary, textColor, textColorSecondary, fontSizeBody, fontWeightBody]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.BUTTON_DESIGN}
                                                detectChangeValues={[buttonVariant, buttonType, buttonTextType, buttonEffects, buttonFont, buttonThickness]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.INPUT_DESIGN}
                                                detectChangeValues={[inputVariant, inputType, checkboxType]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.DESIGN}
                                                detectChangeValues={[colorPrimary, overallType]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.LOGIN}
                                                detectChangeValues={[orderIdText, orderIdPrefix, returnPolicyUrl, termsAndConditionsUrl, privacyPolicyUrl, cookiesPolicy]}
                                                onClick={updateSelectedDetails}
                                            />
                                            <LineDivider />
                                            <SettingCategoryButton
                                                settingsCategory={APP_DESIGN_SETTING_CATEGORY.OTHER}
                                                detectChangeValues={[successConfetti, productImageAspectRatio, productImageObjectFit, exchangePropertyPicker]}
                                                onClick={updateSelectedDetails}
                                            />
                                            {isUserAdmin && (
                                                <>
                                                    <LineDivider />
                                                    <SettingCategoryButton
                                                        settingsCategory={APP_DESIGN_SETTING_CATEGORY.ADMIN}
                                                        detectChangeValues={[merchant, senderEmail, shopSubdomain, exchangePolicy]}
                                                        onClick={updateSelectedDetails}
                                                    />
                                                </>
                                            )}
                                        </TurnrCard>
                                        <div className={classnames(styles["settings-details"], selectedDetailsId && styles["settings-details-show"])}>
                                            <TurnrCard>
                                                <SettingCategoryButton settingsCategory={selectedDetails} onClick={() => updateSelectedDetails(null)} reverseIcons={true} />
                                                <LineDivider />
                                                <TurnrCardContent>
                                                    {selectedDetails?.description && (
                                                        <div
                                                            style={{
                                                                fontSize: "12px",
                                                            }}
                                                            className={"muted mb4 mt1"}
                                                        >
                                                            {selectedDetails?.description}
                                                        </div>
                                                    )}
                                                    {DetailsComponent && (
                                                        <div className={"mt1"}>
                                                            <DetailsComponent {...allInputValuesAndSetters} supportEmailError={supportEmailError} iFrameData={iFrameData} appProps={appProps} />
                                                        </div>
                                                    )}
                                                </TurnrCardContent>
                                            </TurnrCard>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className={"mt3"}>
                                <TurnrCard style={{ borderRadius: 0 }}>
                                    <TurnrGrid container spacing={0} justifyContent="space-between" alignItems="space-between">
                                        <TurnrGrid item xs={6}>
                                            <TurnrButton fullWidth={true} variant="outlined" style={{ borderRadius: 0 }} disabled={Object.keys(serviceBody).length === 0} onClick={onResetClick}>
                                                Reset
                                            </TurnrButton>
                                        </TurnrGrid>
                                        <TurnrGrid item xs={6}>
                                            <TurnrButton
                                                type="submit"
                                                value="Submit"
                                                fullWidth={true}
                                                style={{ borderRadius: 0 }}
                                                disabled={Object.keys(serviceBody).length === 0}
                                                fontAwesomeIcon={faSave}
                                            >
                                                Save
                                            </TurnrButton>
                                        </TurnrGrid>
                                    </TurnrGrid>
                                </TurnrCard>
                            </div>
                            <div className={"mt3"}>
                                <TurnrCard>
                                    <TurnrActionAlert style={{ border: "none" }} showIcon={false} fullWidth={true}>
                                        Go ahead and try your saved settings offline or live
                                    </TurnrActionAlert>
                                    <LineDivider />
                                    <div
                                        style={{
                                            display: "flex",
                                            position: "relative",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <TurnrTooltip
                                            title={"Demo App"}
                                            style={{ flex: "1 1 auto" }}
                                            content={"Opens a new tab with where you can try your saved settings in a demo version of your app. All data here is offline"}
                                        >
                                            <TurnrButton fullWidth={true} runningLine={false} openBlankPage variant={"text"} size={"small"} href={getBrandReturnAppDemoUrl(appProps)}>
                                                Demo App
                                            </TurnrButton>
                                        </TurnrTooltip>
                                        <div
                                            style={{
                                                height: "100%",
                                                width: "1px",
                                                background: "#d7d8db",
                                                position: "absolute",
                                                top: 0,
                                                left: "calc(50% + 2px)",
                                            }}
                                        ></div>

                                        <TurnrTooltip
                                            title={"Live App"}
                                            style={{ flex: "1 1 auto" }}
                                            content={"Opens a new tab with where you can try your saved settings in the live version of your app. You will need an actual order and email to login"}
                                        >
                                            <TurnrButton fullWidth={true} runningLine={false} openBlankPage variant={"text"} size={"small"} href={getBrandReturnAppLiveUrl(appProps)}>
                                                Live App
                                            </TurnrButton>
                                        </TurnrTooltip>
                                    </div>
                                </TurnrCard>
                            </div>
                        </TurnrGrid>
                        <TurnrGrid item xs={12}></TurnrGrid>
                    </TurnrGrid>
                    <IframePart appProps={appProps} setIFrameData={setIFrameData} allInputValues={allInputValues} />
                </TurnrGrid>
            </TurnrForm>
        </>
    );
}

export default AppDesignForm;
